/**
 * Server-Sent Events stream consumer using fetch + ReadableStream.
 *
 * Uses the `eventsource-parser` package to handle SSE protocol framing
 * (multi-line data, id/event fields, retry hints) correctly.
 *
 * This is used instead of the native EventSource API because EventSource
 * does not support custom headers (needed for Authorization: Bearer).
 */
import { createParser } from 'eventsource-parser';

/**
 * Reads a ReadableStream of bytes from a text/event-stream response,
 * parses SSE frames, and calls onEvent for each complete event.
 */
export async function parseEventsFromReadableStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: { event: string; data: string }) => void,
  onError: (err?: Error) => void,
  signal?: AbortSignal
): Promise<void> {
  const decoder = new TextDecoder('utf-8');
  const reader = body.getReader();

  const parser = createParser({
    onEvent(event) {
      onEvent({
        event: event.event ?? 'message',
        data: event.data,
      });
    },
  });

  try {
    while (true) {
      // Check for external abort before each read
      if (signal?.aborted) {
        break;
      }

      const { done, value } = await reader.read();
      if (done) break;

      parser.feed(decoder.decode(value, { stream: true }));
    }
  } catch (err) {
    if (err instanceof Error && err.name !== 'AbortError') {
      onError(err);
    }
  } finally {
    reader.releaseLock();
  }
}
